import { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { Calendar, Export } from '@phosphor-icons/react';
import { useAppDispatch } from '../../../../redux/store';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { fetchBuyerInvoiceVolume } from '../../../../redux/services/buyersService';
import { DillTable, DillButton, type DillColumn } from '../../../general/Dill';
import AppDateRangePickerModal from '../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import InvoiceVolumeChart from '../MainBuyerDashboard/InvoiceVolumeChart';

interface MonthRow {
  key: string;
  month: string;
  invoiceCount: number;
  totalInvoiced: number;
}

interface YearRow {
  key: string;
  year: string;
  invoiceCount: number;
  totalInvoiced: number;
}

const formatDollars = (value: number) =>
  `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const countColumn = <T extends { invoiceCount: number }>(): DillColumn<T> => ({
  key: 'invoiceCount',
  label: 'Invoices',
  width: 120,
  sortable: true,
  numeric: true,
  headerAlign: 'right',
  render: (value: number) => (
    <div className="text-xs text-right tabular-nums text-gray-500">{value || '—'}</div>
  ),
});

const totalColumn = <T extends { totalInvoiced: number }>(): DillColumn<T> => ({
  key: 'totalInvoiced',
  label: 'Total Invoiced',
  width: 160,
  sortable: true,
  numeric: true,
  headerAlign: 'right',
  render: (value: number) =>
    value ? (
      <div className="text-xs font-medium text-right tabular-nums text-gray-700">
        {formatDollars(value)}
      </div>
    ) : (
      <div className="text-xs text-gray-300 text-right">—</div>
    ),
});

const monthColumns: DillColumn<MonthRow>[] = [
  {
    key: 'month',
    label: 'Month',
    width: 160,
    sortable: true,
    render: (value: string) => (
      <div className="text-xs text-gray-700">{moment(value, 'YYYY-MM').format('MMMM YYYY')}</div>
    ),
  },
  countColumn<MonthRow>(),
  totalColumn<MonthRow>(),
];

const yearColumns: DillColumn<YearRow>[] = [
  {
    key: 'year',
    label: 'Year',
    width: 160,
    sortable: true,
    render: (value: string) => <div className="text-xs text-gray-700">{value}</div>,
  },
  countColumn<YearRow>(),
  totalColumn<YearRow>(),
];

const InvoiceVolumeReport = () => {
  const dispatch = useAppDispatch();
  const [selectedMainBuyerId] = useParamMainBuyerId();

  // Default to the start of last year so this year can be compared against a full prior year.
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear() - 1, 0, 1),
    endDate: new Date(),
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [months, setMonths] = useState<MonthRow[]>([]);
  const [years, setYears] = useState<YearRow[]>([]);

  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedMainBuyerId) return;
    let isCurrent = true;
    setIsLoading(true);
    setErrorMessage('');
    dispatch(
      fetchBuyerInvoiceVolume({
        mainBuyerId: selectedMainBuyerId,
        startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
        endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
      })
    ).then((result: any) => {
      // Ignore a response for a date range the user has already changed away from.
      if (!isCurrent) return;
      const data = result?.payload?.data;
      if (result?.error || !data) {
        setMonths([]);
        setYears([]);
        setErrorMessage(
          typeof result?.payload === 'string' ? result.payload : 'Failed to load invoice volume'
        );
      } else {
        setMonths(data.months.map((m: Omit<MonthRow, 'key'>) => ({ ...m, key: m.month })));
        setYears(data.years.map((y: Omit<YearRow, 'key'>) => ({ ...y, key: y.year })));
      }
      setIsLoading(false);
    });
    return () => {
      isCurrent = false;
    };
  }, [selectedMainBuyerId, dateRange.startDate, dateRange.endDate, dispatch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setIsExportOpen(false);
      }
    };
    if (isExportOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExportOpen]);

  const totalInvoiced = useMemo(() => years.reduce((sum, y) => sum + y.totalInvoiced, 0), [years]);
  const invoiceCount = useMemo(() => years.reduce((sum, y) => sum + y.invoiceCount, 0), [years]);

  const chartData = useMemo(
    () =>
      months.map((m) => ({
        month: `${m.month.slice(0, 4)}-${moment(m.month, 'YYYY-MM').format('MMM').toUpperCase()}`,
        totalInvoiced: m.totalInvoiced,
      })),
    [months]
  );

  const formatDateRangeDisplay = () => {
    const start = moment(dateRange.startDate).format('MMM DD, YYYY');
    const end = moment(dateRange.endDate).format('MMM DD, YYYY');
    return `${start} – ${end}`;
  };

  const exportSheets = () => ({
    byMonth: months.map((m) => ({
      Month: moment(m.month, 'YYYY-MM').format('MMMM YYYY'),
      Invoices: m.invoiceCount,
      'Total Invoiced': m.totalInvoiced,
    })),
    byYear: years.map((y) => ({
      Year: y.year,
      Invoices: y.invoiceCount,
      'Total Invoiced': y.totalInvoiced,
    })),
  });

  const handleExportXlsx = () => {
    const { byMonth, byYear } = exportSheets();
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byMonth), 'By Month');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byYear), 'By Year');
    XLSX.writeFile(wb, 'buyer-invoice-volume-report.xlsx');
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    const csv = Papa.unparse(exportSheets().byMonth);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'buyer-invoice-volume-by-month.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsExportOpen(false);
  };

  const exportDropdown = (
    <div
      className="relative"
      ref={exportRef}
      title={isLoading ? 'Still loading invoice volume…' : undefined}>
      <DillButton
        label="Export"
        onClick={() => setIsExportOpen(!isExportOpen)}
        icon={<Export size={14} />}
        variant="primary"
        isDisabled={isLoading || months.length === 0}
      />
      {isExportOpen && (
        <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-48">
          <button
            onClick={handleExportXlsx}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
            Export as XLSX
          </button>
          <button
            onClick={handleExportCsv}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
            Export months as CSV
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col w-full h-[calc(100vh-30px)] overflow-y-auto rounded-xl shadow-sm">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 px-4 sm:px-6 pt-4 sm:pt-6 pb-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-800">Invoice Volume Report</h2>
            <p className="text-xs text-gray-500 mt-1">
              Total dollars invoiced by month and by year, based on invoice date. Includes every
              invoice in the period, open or paid.
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setIsDatePickerOpen(true)}
              className="inline-flex items-center gap-2 h-[34px] px-3.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
              <Calendar size={16} weight="bold" />
              <span>{formatDateRangeDisplay()}</span>
            </button>
            {exportDropdown}
          </div>
        </div>

        {errorMessage && (
          <div className="mx-4 sm:mx-6 mb-4 px-4 py-3 bg-red-50 rounded-lg text-xs text-red-600">
            {errorMessage}
          </div>
        )}

        {/* Summary bar */}
        {!isLoading && !errorMessage && (
          <div className="mx-4 sm:mx-6 mb-4 px-4 py-3 bg-blue-50 rounded-lg flex items-center gap-4 sm:gap-6 flex-wrap">
            <div>
              <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">
                Total Invoiced
              </p>
              <p className="text-sm font-semibold text-blue-800">{formatDollars(totalInvoiced)}</p>
            </div>
            <div className="w-px h-8 bg-blue-200" />
            <div>
              <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">
                Invoices
              </p>
              <p className="text-sm font-semibold text-blue-800">{invoiceCount}</p>
            </div>
            {years.map((y) => (
              <div key={y.year} className="flex items-center gap-4 sm:gap-6">
                <div className="w-px h-8 bg-blue-200" />
                <div>
                  <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">
                    {y.year}
                  </p>
                  <p className="text-sm font-semibold text-blue-800">
                    {formatDollars(y.totalInvoiced)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chart */}
        <div className="px-4">
          {isLoading ? (
            <div className="h-[320px] rounded-lg bg-gray-50 animate-pulse" />
          ) : (
            <InvoiceVolumeChart data={chartData} showYear={years.length > 1} />
          )}
        </div>

        {/* Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-4 px-2 sm:px-4 pb-4">
          <div>
            <h3 className="px-2 pb-2 text-[10px] uppercase tracking-wider text-gray-500 font-medium">
              By Year
            </h3>
            <DillTable<YearRow>
              columns={yearColumns}
              data={years}
              rowKey={(r) => r.key}
              verticalDividers
              search={false}
              scrollX
              rowHeight={35}
              striped={false}
              loading={isLoading}
              loadingText="Loading invoice volume..."
              emptyMessage="No invoices in this period"
              showCount={false}
            />
          </div>
          <div>
            <h3 className="px-2 pb-2 text-[10px] uppercase tracking-wider text-gray-500 font-medium">
              By Month
            </h3>
            <DillTable<MonthRow>
              columns={monthColumns}
              data={months}
              rowKey={(r) => r.key}
              verticalDividers
              search={false}
              scrollX
              rowHeight={35}
              striped={false}
              loading={isLoading}
              loadingText="Loading invoice volume..."
              emptyMessage="No invoices in this period"
              showCount={false}
            />
          </div>
        </div>
      </div>

      <AppDateRangePickerModal
        open={isDatePickerOpen}
        handleClose={() => setIsDatePickerOpen(false)}
        onSelectDateRange={(range) => {
          setDateRange({
            startDate: range.startDate ?? dateRange.startDate,
            endDate: range.endDate ?? dateRange.endDate,
          });
          setIsDatePickerOpen(false);
        }}
        selectedDateRange={dateRange}
      />
    </div>
  );
};

export default InvoiceVolumeReport;
